import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, X, Send, Copy } from "lucide-react";

// In-panel reply composer. Opens the admin's own email client (mailto) pre-filled.
// Real in-app sending can be layered on later once email delivery (Resend) is configured.
export default function ReplyButton({ email, name = "", defaultSubject = "", greeting, testid }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(
    greeting || `Hi ${name || "there"},\n\n\n\n— South East Collectibles`
  );

  const send = () => {
    if (!email) { toast.error("No email address on file for this contact."); return; }
    const href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const a = document.createElement("a");
    a.href = href;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Opening your email app…");
    setOpen(false);
  };

  const copyBody = async () => {
    try { await navigator.clipboard.writeText(body); toast.success("Message copied."); }
    catch { toast.error("Copy failed."); }
  };

  const inputClass = "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors text-sm";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-4 py-2 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors"
        data-testid={testid || "reply-btn"}
      >
        <Mail size={13} /> Reply
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] grid place-items-center p-5 bg-black/80 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="reply-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#121212] border border-white/10 p-7 sm:p-8"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Reply</span>
                  <h3 className="font-display text-2xl font-semibold mt-1">{name || "Reply to contact"}</h3>
                  <p className="font-mono text-xs text-zinc-400 mt-1.5">{email}</p>
                </div>
                <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" data-testid="reply-close" aria-label="Close"><X size={22} /></button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Subject</label>
                  <input className={inputClass} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="reply-subject" />
                </div>
                <div>
                  <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">Message</label>
                  <textarea rows={7} className={`${inputClass} resize-none leading-relaxed`} value={body} onChange={(e) => setBody(e.target.value)} data-testid="reply-body" />
                </div>
                <p className="text-zinc-500 text-xs">This opens your email app (Gmail, Outlook, Mail) with the message ready to send from your own address.</p>
                <div className="flex items-center gap-3">
                  <button onClick={send} className="inline-flex items-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors" data-testid="reply-send">
                    <Send size={14} /> Open In Email App
                  </button>
                  <button onClick={copyBody} className="inline-flex items-center gap-2 rounded-full border border-[#3a3a3a] hover:border-gold hover:text-gold px-5 py-3.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors" data-testid="reply-copy">
                    <Copy size={14} /> Copy
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
